// ============================================
// SELECTORMONEDA.JS — Toggle global MXN/USD + hook useMoneda
// ============================================
// Estado compartido en localStorage + CustomEvent 'beep-moneda-change'.
// El TC se consulta a /api/tc/actual con cache de 5 min.

import React, { useCallback, useEffect, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
} from './helpersClientes';

const STORAGE_KEY = 'beep_moneda';
const EVENT_NAME = 'beep-moneda-change';
const TC_CACHE_MS = 5 * 60 * 1000;
const TC_FALLBACK = 19.50;

// Cache a nivel módulo para compartir el fetch del TC entre todos los
// componentes que usan el hook (no queremos N llamadas simultáneas).
let tcCache = { tc: null, tcMercado: null, margen: null, fecha: null, consultadoEn: null, obtenidoHace: 0 };
let tcFetchPromise = null;

function leerMonedaStorage() {
  try {
    const m = localStorage.getItem(STORAGE_KEY);
    if (m === 'USD' || m === 'MXN') return m;
  } catch {}
  return 'MXN';
}

function setMonedaGlobal(nueva) {
  try { localStorage.setItem(STORAGE_KEY, nueva); } catch {}
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { moneda: nueva } }));
}

async function fetchTC(force = false) {
  const ahora = Date.now();
  if (!force && tcCache.tc != null && ahora - tcCache.obtenidoHace < TC_CACHE_MS) {
    return tcCache;
  }
  if (tcFetchPromise) return tcFetchPromise;
  tcFetchPromise = (async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/tc/actual`).then(x => x.json());
      if (r.ok && Number(r.tc)) {
        tcCache = {
          tc: Number(r.tc),                                       // TC de venta (mercado + margen)
          tcMercado: r.tc_mercado != null ? Number(r.tc_mercado) : null,
          margen: r.margen_proteccion != null ? Number(r.margen_proteccion) : null,
          fecha: r.fecha, consultadoEn: r.consultado_en, obtenidoHace: Date.now(),
        };
      } else if (tcCache.tc == null) {
        tcCache = { tc: TC_FALLBACK, tcMercado: null, margen: null, fecha: null, consultadoEn: null, obtenidoHace: Date.now() };
      }
    } catch {
      if (tcCache.tc == null) {
        tcCache = { tc: TC_FALLBACK, tcMercado: null, margen: null, fecha: null, consultadoEn: null, obtenidoHace: Date.now() };
      }
    } finally {
      tcFetchPromise = null;
    }
    return tcCache;
  })();
  return tcFetchPromise;
}

// ============================================
// useMoneda — hook con moneda + formatear + convertir
// ============================================

export function useMoneda() {
  const [moneda, setMonedaState] = useState(leerMonedaStorage());
  const [tc, setTc] = useState(tcCache.tc || TC_FALLBACK);
  const [tcInfo, setTcInfo] = useState(tcCache);

  // Sincroniza el state cuando cambia la moneda en otro componente
  useEffect(() => {
    const onChange = (e) => setMonedaState(e?.detail?.moneda || leerMonedaStorage());
    window.addEventListener(EVENT_NAME, onChange);
    // Storage event (cambios desde otra pestaña)
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setMonedaState(leerMonedaStorage()); };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    let activo = true;
    fetchTC().then(info => {
      if (!activo) return;
      setTc(info.tc);
      setTcInfo(info);
    });
    return () => { activo = false; };
  }, []);

  const setMoneda = useCallback((nueva) => {
    if (nueva !== 'MXN' && nueva !== 'USD') return;
    setMonedaGlobal(nueva);
  }, []);

  const convertir = useCallback((monto, de, a) => {
    const n = Number(monto);
    if (!Number.isFinite(n)) return 0;
    if (de === a) return n;
    const t = tc || TC_FALLBACK;
    if (de === 'MXN' && a === 'USD') return n / t;
    if (de === 'USD' && a === 'MXN') return n * t;
    return n;
  }, [tc]);

  const formatear = useCallback((monto, monedaOrigen = 'MXN', opciones = {}) => {
    const { decimales, incluirSigla = true } = opciones;
    const convertido = convertir(monto, monedaOrigen, moneda);
    // SIN-REDONDEO (2026-10-12): SIEMPRE >= 2 decimales, en MXN y en USD. Antes, en MXN, todo
    // importe >= 100 perdia los centavos ("$3,132 MXN" junto a un campo que decia 3,132.09).
    // `decimales` solo puede PEDIR mas de 2 (p. ej. 4 para un tipo de cambio), nunca menos.
    const dec = decimales != null ? Math.max(Number(decimales) || 0, 2) : 2;
    const formato = new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(convertido);
    return incluirSigla ? `$${formato} ${moneda}` : `$${formato}`;
  }, [moneda, convertir]);

  return { moneda, setMoneda, tc, tcInfo, formatear, convertir };
}

// ============================================
// <SelectorMoneda /> — componente visual
// ============================================

export default function SelectorMoneda({ compacto = false, mostrarTC = true }) {
  const { moneda, setMoneda, tc, tcInfo } = useMoneda();

  const btn = (m) => ({
    padding: compacto ? '4px 10px' : '6px 14px',
    fontSize: compacto ? 11 : 12,
    fontWeight: 600,
    fontFamily: PP,
    cursor: 'pointer',
    border: 'none',
    background: moneda === m ? COLOR_OSCURO : 'transparent',
    color: moneda === m ? '#fff' : COLOR_GRIS,
    transition: 'all 0.15s',
    // Area tactil 44x44 (UI-1.D); el texto MXN/USD no cambia de tamano.
    minWidth: 44, minHeight: 44,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      fontFamily: PP,
    }}>
      {mostrarTC && tc && (
        <div style={{
          fontSize: compacto ? 10 : 11, color: COLOR_GRIS_CLARO,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
        }}>
          <div>TC venta</div>
          <div style={{ fontWeight: 700, color: COLOR_OSCURO, fontSize: compacto ? 11 : 12 }}>
            ${tc.toFixed(2)}
          </div>
          {tcInfo?.tcMercado != null && tcInfo?.margen != null && (
            <div style={{ fontSize: compacto ? 9 : 10 }}>
              merc ${tcInfo.tcMercado.toFixed(2)} + ${tcInfo.margen.toFixed(2)}
            </div>
          )}
        </div>
      )}
      <div style={{
        display: 'inline-flex',
        border: `1px solid ${COLOR_BORDE}`,
        borderRadius: 99,
        overflow: 'hidden',
        background: '#fff',
      }}>
        <button style={{ ...btn('MXN'), borderRadius: '99px 0 0 99px' }} onClick={() => setMoneda('MXN')}>MXN</button>
        <button style={{ ...btn('USD'), borderRadius: '0 99px 99px 0' }} onClick={() => setMoneda('USD')}>USD</button>
      </div>
    </div>
  );
}
